
import { useEffect, useState, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { WebhookEvent } from "./webhook/eventUtils";
import { WebhookHeader } from "./webhook/WebhookHeader";
import { WebhookEventsTable } from "./webhook/WebhookEventsTable";
import { RealtimeChannel } from "@supabase/supabase-js";

export const WebhookMonitor = () => {
  const [events, setEvents] = useState<WebhookEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const { toast } = useToast();
  const channelRef = useRef<RealtimeChannel | null>(null);
  const userIdRef = useRef<string | null>(null);

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast({
          title: "Authentication error",
          description: "You must be logged in to view webhook events",
          variant: "destructive",
        });
        return;
      }

      userIdRef.current = user.id;

      const { data, error } = await supabase
        .from('webhook_events')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) {
        throw error;
      }

      setEvents(data || []);
      setLastUpdated(new Date());
      
      // Show success toast only if we got data
      if (data && data.length > 0) {
        toast({
          title: "Webhook Events Updated",
          description: `Found ${data.length} events`,
        });
      }
    } catch (error: any) {
      toast({
        title: "Error fetching webhook events",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  // Set up the realtime subscription
  useEffect(() => {
    fetchEvents();

    return () => {
      // Cleanup function to remove the channel when component unmounts
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [fetchEvents]);

  // Handle real-time subscription separately after we have the userId
  useEffect(() => {
    if (!userIdRef.current) return;

    // First, clean up any existing channel
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
    }

    // Set up a new channel with the user filter
    const channel = supabase
      .channel('webhook-events-' + Date.now()) // Unique channel name to avoid conflicts
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'webhook_events',
          filter: `user_id=eq.${userIdRef.current}`,
        },
        (payload) => {
          // Cast the new event to ensure type compatibility
          const newEvent = payload.new as WebhookEvent;
          
          setEvents((current) => {
            // Avoid duplicate events
            const isDuplicate = current.some(event => event.id === newEvent.id);
            if (isDuplicate) return current;
            
            // Add new event and limit to 50
            return [newEvent, ...current].slice(0, 50);
          });
          
          setLastUpdated(new Date());
          
          toast({
            title: "New Webhook Event",
            description: `Received ${newEvent.event_type || 'unknown'} event`,
          });
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          console.log('Successfully subscribed to webhook events');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('Failed to subscribe to webhook events');
        }
      });

    // Store the channel reference for cleanup
    channelRef.current = channel;

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userIdRef.current, toast]);

  return (
    <Card className="p-6">
      <WebhookHeader 
        lastUpdated={lastUpdated} 
        loading={loading} 
        onRefresh={fetchEvents} 
      />
      <WebhookEventsTable events={events} />
    </Card>
  );
};
